import { useCallback, useState } from 'react';

type ModalDataType = any;

export type ModalStackType<ModalTypes extends string> = {
	[key in ModalTypes]: ModalDataType;
};

export type ShowModalType<ModalTypes> = (modalType: ModalTypes, modalData?: any) => void;
export type ShowConfirmModalType<ModalTypes> = (modalType: ModalTypes) => Promise<any>;
export type CloseModalType<ModalTypes> = (modalType: ModalTypes) => void;
export type UpdateModalDataType<ModalTypes> = (modalType: ModalTypes, modalData?: any) => void;

export type useModalsReturnType<ModalTypes extends string> = {
	modalStack: ModalStackType<ModalTypes>;
	showModal: ShowModalType<ModalTypes>;
	showConfirmModal: ShowConfirmModalType<ModalTypes>;
	closeModal: CloseModalType<ModalTypes>;
	updateModalData: UpdateModalDataType<ModalTypes>;
};

export const useModals = <ModalTypes extends string>(): useModalsReturnType<ModalTypes> => {
	const [modalStack, setModalStack] = useState<ModalStackType<ModalTypes>>(
		{} as ModalStackType<ModalTypes>,
	);

	const showModal = useCallback(
		(modalType: any, modalData = {}) => {
			setModalStack((previousStack) => {
				const updatedModalStack = { ...previousStack };
				updatedModalStack[modalType] = {
					...previousStack[modalType],
					...modalData,
				};
				return updatedModalStack;
			});
		},
		[setModalStack],
	);

	const closeModal = useCallback(
		(modalType: any) => {
			setModalStack((previousStack) => {
				const updatedModalStack = { ...previousStack };
				delete updatedModalStack[modalType];
				return updatedModalStack;
			});
		},
		[setModalStack],
	);

	const showConfirmModal = useCallback(
		(modalType: any) => {
			return new Promise((resolve) => {
				showModal(modalType, {
					onConfirm: (data) => {
						closeModal(modalType);
						resolve(data || true);
					},
					onCancel: () => {
						closeModal(modalType);
						resolve(false);
					},
				});
			});
		},
		[showModal, closeModal],
	);

	const updateModalData = useCallback(
		(modalType: any, modalData: any) => {
			setModalStack((previousStack) => {
				const updatedModalStack = { ...previousStack };
				if (updatedModalStack[modalType]) {
					updatedModalStack[modalType] = {
						...previousStack[modalType],
						...modalData,
					};
				}
				return updatedModalStack;
			});
		},
		[setModalStack],
	);

	return {
		modalStack,
		showModal,
		showConfirmModal,
		closeModal,
		updateModalData,
	};
};
